var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import React, { Fragment, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Grid, IconButton, LinearProgress, Typography } from '@material-ui/core';
import DeleteIcon from '@material-ui/icons/Delete';
import Alert from '@material-ui/lab/Alert';
import { useFormikContext, useField } from 'formik';
import { defaultPresignEndPoint, useSecureFileUrl, useUploadFile } from '@sierralabs/react-material-ui';
const StyledGrid = styled(Grid) `
  display: flex;
  flex-direction: column;
  /* align-items: stretch; */
  .inline-image-container {
    position: relative;
    border: solid 1px #ccc;
    border-radius: 4px;
    display: flex;
    flex: 1;
    min-height: 96px;
    .image-wrapper {
    }
    img {
      position: absolute;
      object-fit: contain;
      width: 90%;
      height: 90%;
    }
    button {
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      width: 100%;
      display: flex;
      align-items: center;
      justify-content: center;
    }
  }
`;
export var InlineImageFit;
(function (InlineImageFit) {
    InlineImageFit["Fill"] = "fill";
    InlineImageFit["Contain"] = "contain";
    InlineImageFit["Cover"] = "cover";
})(InlineImageFit || (InlineImageFit = {}));
export const InlineImage = ({ name, label, s3Path, height, grid, title, description, disabled, resize, backgroundColor }) => {
    if (!grid) {
        // default field to expand entire width of form
        grid = { xs: 12 };
    }
    const [open, setOpen] = useState(false);
    const [image, setImage] = useState();
    const [loading, setLoading] = useState(false);
    const formik = useFormikContext();
    const [field, meta, { setValue, setTouched }] = useField(name);
    const secureUrl = useSecureFileUrl(field.value);
    // load the image with retry logic in case S3 hasn't made file available
    useEffect(() => {
        if (secureUrl) {
            setLoading(true);
            const image = new Image();
            image.onload = () => {
                setImage(image);
                setLoading(false);
            };
            let count = 0;
            let timeout;
            image.onerror = () => {
                // retry up to 10 times
                if (count < 10) {
                    count++;
                    clearTimeout(timeout);
                    timeout = setTimeout(() => {
                        image.src = secureUrl;
                    }, 500);
                }
                else {
                    // stop retrying
                    setLoading(false);
                }
            };
            // start image load
            image.src = secureUrl;
            // on unmount stop retrying
            return () => {
                clearTimeout(timeout);
            };
        }
    }, [secureUrl]);
    return (React.createElement(StyledGrid, Object.assign({ item: true, className: 'inline-text-field' }, grid),
        React.createElement(Typography, { variant: 'h5', gutterBottom: true }, label),
        React.createElement("div", { className: 'inline-image-container' },
            React.createElement(Button, { variant: 'text', color: 'primary', className: 'button', style: { backgroundColor }, onClick: () => setOpen(true), disabled: disabled }, !loading && image && secureUrl ? (React.createElement("img", { src: secureUrl, alt: field.value, className: 'image' })) : loading ? (React.createElement(CircularProgress, { size: 20 })) : ('Upload Image'))),
        open && (React.createElement(InlineImageDialog, { value: secureUrl, open: open, s3Path: s3Path, onCancel: () => setOpen(false), onUploaded: url => {
                setOpen(false);
                setTouched(true);
                setValue(url);
                formik.submitForm();
            }, onClear: () => {
                setOpen(false);
                setValue('');
                setTouched(true);
                formik.submitForm();
            }, title: title, description: description, resize: resize }))));
};
export default InlineImage;
const StyledDragArea = styled.div.attrs(props => ({
    color: props.color || '#f4f4f4'
})) `
  background-color: ${props => props.color};
  position: relative;
  height: 300px;
  border: solid 1px #ccc;
  border-radius: 4px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  &:hover {
    .overlay-bar {
      opacity: 1;
    }
  }
  .or-text {
    margin: 10px 0;
  }
  img {
    object-fit: contain;
    width: 100%;
    height: 100%;
  }
  .overlay-bar {
    opacity: 0;
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    display: flex;
    background: rgba(0, 0, 0, 0.5);
    color: #fff;
    padding: 5px 10px;
    align-items: center;
    transition: 300ms;
    .meta-data {
      flex: 1;
    }
    button {
      margin-right: 5px;
    }
  }
`;
const StyledOverlayButton = styled(Button) `
  border: 1px solid #fff;
  color: #fff;
`;
const StyledOverlayIconButton = styled(IconButton) `
  color: #fff;
`;
export const InlineImageDialog = ({ value, s3Path, open, disabled, onCancel, onClear, onUploaded, title, description, resize }) => {
    const fileInputRef = useRef(null);
    const [dragover, setDragover] = useState(false);
    const onUploadedRef = useRef(onUploaded);
    onUploadedRef.current = onUploaded;
    const [file, setFile] = useState();
    const [imageUrl, setImageUrl] = useState(value);
    const [isCleared, setIsCleared] = useState(false);
    const { fileUrl, isUploading, progress, error, upload } = useUploadFile(s3Path, defaultPresignEndPoint);
    // when upload complete load image before submitting (S3 upload has delay
    // before the image is accessible)
    const secureFileUrl = useSecureFileUrl(fileUrl);
    const [loading, setLoading] = useState(false);
    useEffect(() => {
        if (secureFileUrl && fileUrl && progress === 1 && !isUploading) {
            // load the image with retry logic in case S3 hasn't made file available
            setLoading(true);
            const image = new Image();
            image.onload = () => {
                onUploadedRef.current(fileUrl);
                setLoading(false);
            };
            let count = 0;
            let timeout;
            image.onerror = () => {
                // retry up to 10 times
                if (count < 10) {
                    count++;
                    clearTimeout(timeout);
                    timeout = setTimeout(() => {
                        image.src = secureFileUrl;
                    }, 500);
                }
                else {
                    // stop retrying
                    setLoading(false);
                }
            };
            // start image load
            image.src = secureFileUrl;
            // on unmount stop retrying
            return () => {
                clearTimeout(timeout);
            };
        }
    }, [secureFileUrl, fileUrl, isUploading, progress]);
    const handleFile = (files) => __awaiter(void 0, void 0, void 0, function* () {
        if (files && files.length > 0) {
            const file = files[0];
            const dataUrl = yield readImageFile(file);
            setIsCleared(false);
            setFile(file);
            setImageUrl(dataUrl);
            if (resize &&
                file.type !== 'image/gif' /* don't resize animated gifs */) {
                if (!resize.type) {
                    resize.type = file.type;
                }
                let image = yield loadImageUri(dataUrl);
                image = yield resizeImage(image, resize);
                const blob = convertUriToBlob(image.src);
                setFile(new File([blob], file.name, { type: resize.type }));
                setImageUrl(image.src);
            }
        }
    });
    return (React.createElement(Dialog, { open: open, onClose: onCancel },
        React.createElement(DialogTitle, null, title || 'Upload Image'),
        description && React.createElement(DialogContent, null, description),
        React.createElement(DialogContent, null,
            React.createElement(StyledDragArea, { className: 'drag-area', color: dragover ? '#d3d3d3' : 'white', onDragOver: event => {
                    event.preventDefault();
                    if (disabled)
                        return;
                    setDragover(true);
                }, onDragLeave: () => !disabled && setDragover(false), onDrop: event => {
                    event.preventDefault();
                    if (disabled)
                        return;
                    setDragover(false);
                    handleFile(event.dataTransfer.files);
                } },
                imageUrl ? (React.createElement(Fragment, null,
                    React.createElement("img", { src: imageUrl, alt: 'selected file to upload' }),
                    React.createElement("div", { className: 'overlay-bar' },
                        React.createElement("div", { className: 'meta-data' },
                            "File Size: ", file === null || file === void 0 ? void 0 :
                            file.size.toLocaleString(),
                            " bytes"),
                        React.createElement(StyledOverlayButton, { variant: 'outlined', size: 'small', onClick: () => { var _a; return (_a = fileInputRef.current) === null || _a === void 0 ? void 0 : _a.click(); } }, "Change File"),
                        React.createElement(StyledOverlayIconButton, { size: 'small', onClick: () => {
                                setFile(undefined);
                                setImageUrl(undefined);
                                setIsCleared(true);
                            } },
                            React.createElement(DeleteIcon, null))))) : (React.createElement(Fragment, null,
                    React.createElement(Typography, { variant: 'subtitle2' }, "Drag a file here"),
                    React.createElement(Typography, { variant: 'subtitle2', className: 'or-text' }, "-- or --"),
                    React.createElement(Button, { variant: 'outlined', color: 'primary', onClick: () => { var _a; return (_a = fileInputRef.current) === null || _a === void 0 ? void 0 : _a.click(); } }, "Select File"))),
                React.createElement("input", { hidden: true, type: 'file', accept: 'image/*', ref: fileInputRef, onChange: event => handleFile(event.target.files) })),
            (loading || isUploading) && (React.createElement(LinearProgress, { color: 'primary', variant: 'determinate', value: progress * 100 })),
            error && (React.createElement(Box, { mt: 2 },
                React.createElement(Alert, { severity: 'error' }, "Error uploading. Please check the internet connection and try again.")))),
        React.createElement(DialogActions, null,
            React.createElement(Button, { onClick: onCancel }, "Cancel"),
            isCleared ? (React.createElement(Button, { variant: 'contained', color: 'primary', onClick: () => onClear() }, "Clear Image")) : (React.createElement(Button, { variant: 'contained', color: 'primary', disabled: !file || isUploading || loading, onClick: () => upload(file) }, "Upload")))));
};
const readImageFile = (file) => {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.readAsDataURL(file); // read file as data url
        reader.addEventListener('load', () => resolve(reader.result), false);
        reader.addEventListener('error', error => {
            reject(error);
        }, false);
    });
};
const loadImageUri = (imageUri) => {
    return new Promise((resolve, reject) => {
        const image = new Image();
        image.src = imageUri;
        image.onload = () => {
            resolve(image);
        };
        image.onerror = error => {
            reject(error);
        };
    });
};
export const resizeImage = (image, resize) => __awaiter(void 0, void 0, void 0, function* () {
    const result = {};
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');
    if (context === null) {
        throw new Error('Unable to get 2D Canvas context.');
    }
    // let orientation = EXIF.getTag(this, 'Orientation');
    // console.log('EXIF', EXIF.pretty(this))
    let width = resize.width ? resize.width : image.width;
    let height = resize.height ? resize.height : image.height;
    if (!resize.width && resize.maxWidth && image.width > resize.maxWidth) {
        width = resize.maxWidth;
    }
    if (!resize.width && resize.minWidth && image.width < resize.minWidth) {
        width = resize.minWidth;
    }
    if (!resize.height && resize.maxHeight && image.height > resize.maxHeight) {
        height = resize.maxHeight;
    }
    if (!resize.height && resize.minHeight && image.height < resize.minHeight) {
        height = resize.minHeight;
    }
    if (resize.warnSmallImage && (image.width < width || image.height < height)) {
        result.warningMessage =
            'Selected image is smaller then intended size. Image may appear pixelated or blurry.';
    }
    else if (resize.warnAspectRatio &&
        width / image.width !== height / image.height) {
        result.warningMessage = 'Incorrect aspect ratio';
    }
    switch (resize.fit) {
        case InlineImageFit.Cover:
            {
                canvas.width = width;
                canvas.height = height;
                const scale = Math.max(width / image.width, height / image.height);
                width = image.width * scale;
                height = image.height * scale;
            }
            break;
        case InlineImageFit.Contain:
            {
                let scale = Math.min(width / image.width, height / image.height);
                if (scale > 1.0) {
                    scale = 1.0;
                } // don't enlarge the image
                width = image.width * scale;
                height = image.height * scale;
                canvas.width = width;
                canvas.height = height;
            }
            break;
        default:
            canvas.width = width;
            canvas.height = height;
            break;
    }
    // context.save();
    // self.setOrientation(canvas, context, width, height, orientation);
    context.drawImage(image, 0, 0, width, height);
    // context.restore();
    const imageUri = canvas.toDataURL(resize.type || 'image/jpeg', resize.quality || 0.8);
    return loadImageUri(imageUri);
});
export const convertUriToBlob = (dataURI) => {
    // convert base64/URLEncoded data component to raw binary data held in a string
    let byteString;
    if (dataURI.split(',')[0].indexOf('base64') >= 0) {
        byteString = atob(dataURI.split(',')[1]);
    }
    else {
        byteString = unescape(dataURI.split(',')[1]);
    }
    // separate out the mime component
    const mimeString = dataURI.split(',')[0].split(':')[1].split(';')[0];
    // write the bytes of the string to a typed array
    const ia = new Uint8Array(byteString.length);
    for (let i = 0; i < byteString.length; i++) {
        ia[i] = byteString.charCodeAt(i);
    }
    return new Blob([ia], { type: mimeString });
};
//# sourceMappingURL=InlineImage.js.map